import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    AlertCircle,
    Plus,
    ChevronDown,
    RefreshCw,
    Save
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
    validateNumberInput,
    getCharacterCount,
    FIELD_LIMITS,
    NUMBER_LIMITS
} from '../../../utils/inputValidation';
import assignmentRuleService from '../../../api/crm/assignment-rules/assignmentRuleService';
import { industryService, leadSourceService, companySizeService } from '../../../api/crm/config/configService';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { toast } from '../../../utils/toast';

const AssignmentRuleModal = ({
    isOpen,
    onClose,
    ruleToEdit,
    users,
    onSearchUsers,
    loadingUsers,
    filteredUsers,
    canModify,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        assignmentType: 'round_robin',
        conditions: [],
        assignees: [],
        priority: 1,
        isActive: true
    });
    const [errors, setErrors] = useState({});
    const [newCondition, setNewCondition] = useState({ field: 'region', operator: 'equals', value: '' });
    const [newAssignee, setNewAssignee] = useState('');
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
    const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');
    const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);
    const [isLoadingLeadSources, setIsLoadingLeadSources] = useState(false);
    const [industryOptions, setIndustryOptions] = useState([]);
    const [isLoadingIndustries, setIsLoadingIndustries] = useState(false);
    const [companySizeOptions, setCompanySizeOptions] = useState([]);
    const [isLoadingCompanySizes, setIsLoadingCompanySizes] = useState(false);
    const assigneeDropdownRef = useRef(null);

    // Field-specific options
    const fieldOptions = {
        source: {
            label: 'Source',
            type: 'enum',
            options: leadSourceOptions,
            operators: ['equals', 'not_equals']
        },
        region: {
            label: 'Region',
            type: 'enum',
            options: [
                { value: 'North', label: 'North' },
                { value: 'South', label: 'South' },
                { value: 'East', label: 'East' },
                { value: 'West', label: 'West' },
                { value: 'Central', label: 'Central' },
                { value: 'North-East', label: 'North-East' },
                { value: 'North-West', label: 'North-West' },
                { value: 'South-East', label: 'South-East' },
                { value: 'South-West', label: 'South-West' }
            ],
            operators: ['equals', 'not_equals']
        },
        company_size: {
            label: 'Company Size',
            type: 'enum',
            options: companySizeOptions,
            operators: ['equals', 'not_equals']
        },
        industry: {
            label: 'Industry',
            type: 'enum',
            options: industryOptions,
            operators: ['equals', 'contains', 'not_equals']
        },
        score: {
            label: 'Score',
            type: 'number',
            operators: ['greater_than', 'less_than', 'equals', 'greater_than_equal', 'less_than_equal']
        },
        leadValue: {
            label: 'Lead Value',
            type: 'number',
            operators: ['greater_than', 'less_than', 'equals', 'greater_than_equal', 'less_than_equal']
        }
    };

    const getCurrentFieldConfig = () => {
        return fieldOptions[newCondition.field] || { type: 'text', operators: ['equals', 'contains'], label: newCondition.field };
    };

    const formatOperator = (operator) => {
        const labels = {
            'equals': 'Equals',
            'not_equals': 'Not Equals',
            'contains': 'Contains',
            'in': 'In (Multiple)',
            'not_in': 'Not In',
            'greater_than': 'Greater Than',
            'less_than': 'Less Than',
            'greater_than_equal': 'Greater Than or Equal',
            'less_than_equal': 'Less Than or Equal',
            'between': 'Between'
        };
        return labels[operator] || operator?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || operator;
    };

    const formatValue = (field, value) => {
        if (!value) return '';
        if (typeof value === 'number') return String(value);

        const fieldConfig = fieldOptions[field];
        if (fieldConfig?.options) {
            const option = fieldConfig.options.find(opt => String(opt.value) === String(value));
            if (option) return option.label;
        }

        return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const fetchLeadSourceOptions = async () => {
        if (leadSourceOptions.length > 0 || isLoadingLeadSources) {
            return;
        }

        try {
            setIsLoadingLeadSources(true);
            const response = await leadSourceService.getAll({ isActive: true });

            if (response?.success && Array.isArray(response.data)) {
                const options = response.data.map((source) => ({
                    value: source.leadSourceCode || source.name,
                    label: source.name
                }));
                setLeadSourceOptions(options);
            } else {
                setLeadSourceOptions([]);
            }
        } catch (error) {
            toast.error('Failed to load lead sources');
            setLeadSourceOptions([]);
        } finally {
            setIsLoadingLeadSources(false);
        }
    };

    const fetchIndustryOptions = async () => {
        if (industryOptions.length > 0 || isLoadingIndustries) {
            return;
        }

        try {
            setIsLoadingIndustries(true);
            const response = await industryService.getAll({ isActive: true });

            if (response?.success && Array.isArray(response.data)) {
                const options = response.data.map((industry) => ({
                    value: industry.name,
                    label: industry.name
                }));
                setIndustryOptions(options);
            } else {
                setIndustryOptions([]);
            }
        } catch (error) {
            toast.error('Failed to load industries');
            setIndustryOptions([]);
        } finally {
            setIsLoadingIndustries(false);
        }
    };

    const fetchCompanySizeOptions = async () => {
        if (companySizeOptions.length > 0 || isLoadingCompanySizes) {
            return;
        }

        try {
            setIsLoadingCompanySizes(true);
            const response = await companySizeService.getAll({ isActive: true });

            if (response?.success && Array.isArray(response.data)) {
                const options = response.data.map((size) => ({
                    value: size.name,
                    label: size.name
                }));
                setCompanySizeOptions(options);
            } else {
                setCompanySizeOptions([]);
            }
        } catch (error) {
            toast.error('Failed to load company sizes');
            setCompanySizeOptions([]);
        } finally {
            setIsLoadingCompanySizes(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            if (ruleToEdit) {
                // Edit mode - assignees should already be IDs from parent
                setFormData({
                    name: ruleToEdit.name,
                    description: ruleToEdit.description || '',
                    assignmentType: ruleToEdit.assignmentType,
                    conditions: ruleToEdit.conditions || [],
                    assignees: ruleToEdit.assignees, // ID array
                    priority: ruleToEdit.priority,
                    isActive: ruleToEdit.isActive
                });
            } else {
                // Create mode
                setFormData({
                    name: '',
                    description: '',
                    assignmentType: 'round_robin',
                    conditions: [],
                    assignees: [],
                    priority: 1,
                    isActive: true
                });
            }
            // Reset new condition
            const defaultFieldConfig = fieldOptions.region;
            setNewCondition({
                field: 'region',
                operator: defaultFieldConfig?.operators[0] || 'equals',
                value: ''
            });
            setErrors({});
            setNewAssignee('');
            setAssigneeSearchTerm('');
        }
    }, [isOpen, ruleToEdit]);

    // Debounce assignee search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAssigneeSearchTerm(assigneeSearchTerm.trim());
        }, 400);
        return () => clearTimeout(timer);
    }, [assigneeSearchTerm]);

    // Trigger search when debounced term changes and dropdown is open
    useEffect(() => {
        if (isOpen && isAssigneeDropdownOpen) {
            onSearchUsers(debouncedAssigneeSearchTerm);
        }
    }, [debouncedAssigneeSearchTerm, isAssigneeDropdownOpen, isOpen, onSearchUsers]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
                setIsAssigneeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Implement onBlur validation for required fields
    const handleBlur = (field, e) => {
        if (field === 'name') {
            if (!formData.name.trim()) {
                setErrors(prev => ({ ...prev, name: 'Rule name is required.' }));
            }
        } else if (field === 'conditions') {
            // Check if focus is leaving the container
            if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
                return;
            }
            if (formData.conditions.length === 0) {
                setErrors(prev => ({ ...prev, conditions: 'Please add at least one condition.' }));
            }
        } else if (field === 'assignees') {
            // Check if focus is leaving the container
            if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
                return;
            }
            if (formData.assignees.length === 0) {
                setErrors(prev => ({ ...prev, assignees: 'Please add at least one assignee.' }));
            }
        }
    };

    const handleFormDataChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
        let processedValue = value;
        if (field === 'assignmentType' || field === 'isActive') {
            processedValue = value;
        } else if (fieldType === 'text' || fieldType === 'textarea') {
            processedValue = String(value || '').replace(INVALID_CHARS_REGEX, '');
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'number') {
            const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
            const minValue = NUMBER_LIMITS[field]?.min || 0;
            const numValidation = validateNumberInput(value, maxValue, minValue);
            processedValue = numValidation.value;
        }

        setFormData(prev => ({ ...prev, [field]: processedValue }));

        // Clear error if value becomes valid
        if (field === 'name' && processedValue && String(processedValue).trim()) {
            setErrors(prev => ({ ...prev, name: undefined }));
        }
    };

    const handleFieldChange = (field) => {
        const fieldConfig = fieldOptions[field];
        setNewCondition({
            field,
            operator: fieldConfig?.operators[0] || 'equals',
            value: ''
        });

        if (field === 'source') {
            fetchLeadSourceOptions();
        }
        if (field === 'industry') {
            fetchIndustryOptions();
        }
        if (field === 'company_size') {
            fetchCompanySizeOptions();
        }
    };

    useEffect(() => {
        if (isOpen && newCondition.field === 'source') {
            fetchLeadSourceOptions();
        }
        if (isOpen && newCondition.field === 'industry') {
            fetchIndustryOptions();
        }
        if (isOpen && newCondition.field === 'company_size') {
            fetchCompanySizeOptions();
        }
    }, [isOpen, newCondition.field]);

    const handleAddCondition = () => {
        setErrors(prev => ({ ...prev, newCondition: undefined }));

        if (newCondition.field && newCondition.operator && newCondition.value) {
            setFormData(prev => ({
                ...prev,
                conditions: [...prev.conditions, { ...newCondition }]
            }));
            setErrors(prev => ({ ...prev, conditions: undefined, newCondition: undefined }));

            const defaultFieldConfig = fieldOptions.region;
            setNewCondition({
                field: 'region',
                operator: defaultFieldConfig?.operators[0] || 'equals',
                value: ''
            });
        } else {
            setErrors(prev => ({ ...prev, newCondition: 'Please select a value.' }));
        }
    };

    const handleRemoveCondition = (index) => {
        setFormData(prev => {
            const newConditions = prev.conditions.filter((_, i) => i !== index);
            if (newConditions.length === 0) {
                // Optional: Set error if list becomes empty? 
                // Usually validation is on submit or on blur. 
                // Just clearing the error if it was set incorrectly is done in handleAdd.
                // If we want immediate feedback on empty list:
                // setErrors(prevErrors => ({ ...prevErrors, conditions: 'Please add at least one condition.' }));
            }
            return {
                ...prev,
                conditions: newConditions
            };
        });
    };

    const handleAddAssignee = () => {
        setErrors(prev => ({ ...prev, newAssignee: undefined }));

        if (newAssignee) {
            if (formData.assignees.includes(newAssignee)) {
                setErrors(prev => ({ ...prev, newAssignee: 'Assignee already added.' }));
                return;
            }
            setFormData(prev => ({
                ...prev,
                assignees: [...prev.assignees, newAssignee]
            }));
            setErrors(prev => ({ ...prev, assignees: undefined, newAssignee: undefined }));
            setNewAssignee('');
            setAssigneeSearchTerm('');
            setDebouncedAssigneeSearchTerm('');
            setIsAssigneeDropdownOpen(false);
            onSearchUsers('');
        } else {
            setErrors(prev => ({ ...prev, newAssignee: 'Please select an assignee.' }));
        }
    };

    const handleRemoveAssignee = (index) => {
        setFormData(prev => {
            const newAssignees = prev.assignees.filter((_, i) => i !== index);
            return {
                ...prev,
                assignees: newAssignees
            };
        });
    };

    const handleSave = async () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Rule name is required.';
        } else if (!VALID_TEXT_REGEX.test(formData.name.trim())) {
            newErrors.name = 'Rule name contains invalid characters.';
        }

        if (formData.description && !VALID_TEXT_REGEX.test(formData.description.trim())) {
            newErrors.description = 'Description contains invalid characters.';
        }

        if (formData.conditions.length === 0) newErrors.conditions = 'Please add at least one condition.';
        if (formData.assignees.length === 0) newErrors.assignees = 'Please add at least one assignee.';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);

        try {
            let result;
            if (ruleToEdit) {
                const ruleId = ruleToEdit._id || ruleToEdit.ruleId;
                result = await assignmentRuleService.updateRule(ruleId, formData);
            } else {
                result = await assignmentRuleService.createRule(formData);
            }

            if (result.success) {
                toast.success(`Assignment rule ${ruleToEdit ? 'updated' : 'created'} successfully!`);
                onSuccess();
                onClose();
            } else {
                toast.error(result.error || `Failed to ${ruleToEdit ? 'update' : 'create'} rule`);
            }
        } catch (error) {

            toast.error(`Failed to ${ruleToEdit ? 'update' : 'create'} rule`);
        } finally {
            setIsSaving(false);
        }
    };

    // Filter users to exclude already selected assignees from the dropdown options
    const dropdownOptions = (filteredUsers || []).filter(user => !formData.assignees.includes(user.id));

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {ruleToEdit ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rule Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleFormDataChange('name', e.target.value, 'text', 100)}
                            className={`input-field first-letter:capitalize ${errors.name ? 'border-red-300 ring-red-300' : ''}`}
                            placeholder="Enter rule name"
                            maxLength={100}
                            onBlur={(e) => handleBlur('name', e)}
                        />
                        <div className="flex justify-between mt-1">
                            {errors.name ? (
                                <span className="text-xs text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {errors.name}
                                </span>
                            ) : <span></span>}
                            <span className="text-xs text-gray-500">
                                {getCharacterCount(formData.name, 100)}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleFormDataChange('description', e.target.value, 'textarea', 1500)}
                            className="input-field first-letter:capitalize"
                            placeholder="Enter rule description"
                            maxLength={1500}
                        />
                        <div className="flex justify-end mt-1">
                            <span className="text-xs text-gray-500">
                                {getCharacterCount(formData.description, 1500)}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assignment Type
                        </label>
                        <select
                            value={formData.assignmentType}
                            onChange={(e) => handleFormDataChange('assignmentType', e.target.value)}
                            className="input-field"
                        >
                            <option value="round_robin">Round Robin</option>
                            <option value="manual">Manual Assignment</option>
                        </select>
                    </div>

                    <div onBlur={(e) => handleBlur('conditions', e)}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Conditions <span className="text-red-500 ml-1">*</span>
                            </label>
                            {errors.conditions && (
                                <div className="mb-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {errors.conditions}
                                </div>
                            )}
                            <div className="space-y-3">
                                {formData.conditions.map((condition, index) => {
                                    const fieldConfig = fieldOptions[condition.field] || { label: condition.field };
                                    return (
                                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                            <span className="text-sm font-medium text-gray-700 capitalize">{fieldConfig.label || condition.field}</span>
                                            <span className="text-sm text-gray-500">{formatOperator(condition.operator)}</span>
                                            <span className="text-sm text-gray-700 capitalize">{formatValue(condition.field, condition.value)}</span>
                                            <button
                                                onClick={() => handleRemoveCondition(index)}
                                                className="text-red-500 hover:text-red-700 ml-auto"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    );
                                })}

                                <div className="space-y-3">
                                    <div className="flex items-center space-x-3">
                                        <select
                                            value={newCondition.field}
                                            onChange={(e) => handleFieldChange(e.target.value)}
                                            className="input-field flex-1"
                                        >
                                            <option value="region">Region</option>
                                            <option value="source">Source</option>
                                            <option value="company_size">Company Size</option>
                                            <option value="industry">Industry</option>
                                            <option value="score">Score</option>
                                            <option value="leadValue">Lead Value</option>
                                        </select>
                                        <select
                                            value={newCondition.operator}
                                            onChange={(e) => setNewCondition(prev => ({ ...prev, operator: e.target.value, value: '' }))}
                                            className="input-field flex-1"
                                        >
                                            {getCurrentFieldConfig().operators.map(op => (
                                                <option key={op} value={op}>{formatOperator(op)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        {getCurrentFieldConfig().type === 'enum' ? (
                                            <select
                                                value={newCondition.value}
                                                onChange={(e) => setNewCondition(prev => ({ ...prev, value: e.target.value }))}
                                                className="input-field flex-1"
                                            >
                                                <option value="">Select {getCurrentFieldConfig().label}</option>
                                                {(newCondition.field === 'source' && isLoadingLeadSources) || 
                                                 (newCondition.field === 'industry' && isLoadingIndustries) ||
                                                 (newCondition.field === 'company_size' && isLoadingCompanySizes) ? (
                                                    <option value="" disabled>
                                                        {newCondition.field === 'industry' ? 'Loading industries...' : 
                                                         newCondition.field === 'source' ? 'Loading sources...' : 'Loading company sizes...'}
                                                    </option>
                                                ) : (
                                                    getCurrentFieldConfig().options
                                                        .filter(option =>
                                                            !formData.conditions.some(c =>
                                                                c.field === newCondition.field &&
                                                                c.operator === newCondition.operator &&
                                                                String(c.value) === String(option.value)
                                                            )
                                                        )
                                                        .map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))
                                                )}
                                            </select>
                                        ) : getCurrentFieldConfig().type === 'number' ? (
                                            <input
                                                type="number"
                                                value={newCondition.value}
                                                onChange={(e) => {
                                                    const numValidation = validateNumberInput(e.target.value, Number.MAX_SAFE_INTEGER, 0);
                                                    setNewCondition(prev => ({ ...prev, value: numValidation.value || '' }));
                                                }}
                                                className="input-field flex-1"
                                                placeholder={`Enter ${getCurrentFieldConfig().label.toLowerCase()}`}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={newCondition.value}
                                                onChange={(e) => {
                                                    const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                                                    setNewCondition(prev => ({ ...prev, value: sanitized.slice(0, 200) }));
                                                }}
                                                className="input-field flex-1"
                                                placeholder={`Enter ${getCurrentFieldConfig().label.toLowerCase()}`}
                                                maxLength={200}
                                            />
                                        )}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            icon={<Plus className="h-4 w-4" />}
                                            onClick={handleAddCondition}
                                            className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                        >
                                            Add
                                        </Button>
                                    </div>
                                    {errors.newCondition && (
                                        <div className="mt-2 text-sm text-red-600 flex items-center bg-red-50 p-2 rounded-md border border-red-200">
                                            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                            {errors.newCondition}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div onBlur={(e) => handleBlur('assignees', e)}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assignees <span className="text-red-500 ml-1">*</span>
                            </label>
                            {errors.assignees && (
                                <div className="mb-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {errors.assignees}
                                </div>
                            )}
                            <div className="space-y-2">
                                {formData.assignees.map((assigneeId, index) => {
                                    const assignee = users.find(u => u.id === assigneeId);
                                    const assigneeName = assignee ? assignee.name : assigneeId;
                                    return (
                                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-gray-700 capitalize">{assigneeName}</span>
                                            <button
                                                onClick={() => handleRemoveAssignee(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    );
                                })}

                                <div className="flex items-center space-x-2">
                                    <div className="flex-1 relative" ref={assigneeDropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen);
                                                if (!isAssigneeDropdownOpen) {
                                                    onSearchUsers(assigneeSearchTerm);
                                                }
                                            }}
                                            className="input-field flex items-center justify-between text-left bg-white"
                                        >
                                            <span className="truncate capitalize">
                                                {newAssignee
                                                    ? (() => {
                                                        const selectedUser = users.find(u => u.id === newAssignee);
                                                        return selectedUser
                                                            ? `${selectedUser.name} ${selectedUser.email ? `(${selectedUser.email})` : ''}`
                                                            : 'Select assignee...';
                                                    })()
                                                    : 'Select assignee...'}
                                            </span>
                                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                                        </button>

                                        {isAssigneeDropdownOpen && (
                                            <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                                                <div className="p-2 border-b border-gray-100">
                                                    <div className="flex space-x-2">
                                                        <input
                                                            type="text"
                                                            value={assigneeSearchTerm}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                                                setAssigneeSearchTerm(value);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    onSearchUsers(assigneeSearchTerm.trim());
                                                                }
                                                            }}
                                                            placeholder="Search users..."
                                                            maxLength={100}
                                                            className="input-field text-sm flex-1"
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => onSearchUsers(assigneeSearchTerm.trim())}
                                                            disabled={loadingUsers}
                                                            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                                                        >
                                                            {loadingUsers ? '...' : '↻'}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="overflow-y-auto" style={{ maxHeight: '132px' }}>
                                                    {loadingUsers && dropdownOptions.length === 0 ? (
                                                        <div className="p-3 text-sm text-gray-500 text-center">
                                                            Loading users...
                                                        </div>
                                                    ) : dropdownOptions.length === 0 ? (
                                                        <div className="p-3 text-sm text-gray-500 text-center">
                                                            {assigneeSearchTerm ? 'No users found' : 'Start typing to search'}
                                                        </div>
                                                    ) : (
                                                        dropdownOptions.map(user => (
                                                            <button
                                                                key={user.id}
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setNewAssignee(user.id);
                                                                }}
                                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${user.id === newAssignee ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                                                    }`}
                                                            >
                                                                <div className="truncate capitalize">{user.name}</div>
                                                                {user.email && (
                                                                    <div className="text-xs text-gray-500">{user.email}</div>
                                                                )}
                                                                {user.department && (
                                                                    <div className="text-xs text-gray-500 capitalize">{user.department}</div>
                                                                )}
                                                                {user.designation && (
                                                                    <div className="text-xs text-gray-500 capitalize">{user.designation}</div>
                                                                )}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Plus className="h-4 w-4" />}
                                        onClick={handleAddAssignee}
                                        className="bg-blue-50 border-blue-300 text-[#0B2033] hover:bg-blue-100"
                                        disabled={!newAssignee || loadingUsers}
                                    >
                                        Add
                                    </Button>
                                </div>
                                {errors.newAssignee && (
                                    <div className="mt-2 text-sm text-red-600 flex items-center bg-red-50 p-2 rounded-md border border-red-200">
                                        <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                        {errors.newAssignee}
                                    </div>
                                )}

                                {formData.assignees.length === 0 && (
                                    <div className="text-sm text-gray-500">No assignees selected</div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Priority
                            </label>
                            <select
                                value={formData.priority}
                                onChange={(e) => handleFormDataChange('priority', e.target.value, 'number', 10)}
                                className="input-field"
                                min="1"
                                max="10"
                            >
                                <option value={1}>High (1)</option>
                                <option value={2}>Medium (2)</option>
                                <option value={3}>Low (3)</option>
                            </select>
                        </div>

                        <div className="flex items-center mt-4">
                            <input
                                type="checkbox"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={(e) => handleFormDataChange('isActive', e.target.checked)}
                                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                            />
                            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                                Active Rule
                            </label>
                        </div>
                    </div>

                    <div className="flex items-center justify-end space-x-3 mt-6">
                        <Button
                            className="btn-cancel"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        {canModify && (
                            <Button
                                variant="primary"
                                icon={isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                className="btn-create"
                                onClick={handleSave}
                                disabled={isSaving || !formData.name.trim() || formData.conditions.length === 0 || formData.assignees.length === 0}
                            >
                                {isSaving
                                    ? (ruleToEdit ? 'Updating...' : 'Creating...')
                                    : (ruleToEdit ? 'Update Rule' : 'Create Rule')
                                }
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignmentRuleModal;
